import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';

/**
 * Disables automatic per-frame shadow map updates and only marks the
 * shadow map dirty when controls change (camera movement). Saves the
 * full shadow-pass cost on frames where only mover positions change
 * (movers are too small relative to the shadow frustum to need updates
 * every frame).
 *
 * When `enabled` is false, reverts to Three.js default auto-update.
 */
export function useShadowAutoUpdate(enabled: boolean): void {
  const gl = useThree((s) => s.gl);
  const controls = useThree((s) => s.controls) as
    | { addEventListener?: (type: string, fn: () => void) => void; removeEventListener?: (type: string, fn: () => void) => void }
    | null;

  useEffect(() => {
    if (!enabled) {
      gl.shadowMap.autoUpdate = true;
      return;
    }

    gl.shadowMap.autoUpdate = false;
    gl.shadowMap.needsUpdate = true;

    const handleChange = () => {
      gl.shadowMap.needsUpdate = true;
    };

    controls?.addEventListener?.('change', handleChange);
    return () => {
      controls?.removeEventListener?.('change', handleChange);
      gl.shadowMap.autoUpdate = true;
    };
  }, [enabled, gl, controls]);
}
